import { useCallback } from 'react';
import { useFormSelector } from '@elfsight/widget-form-builder/widget';
import { formatSuffixNumber } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { useCalculations } from '../calculations-context';
import { useCalculatorConditionalLogic } from '../conditional-logic-context';
import { formatCalculatorValue } from '../utils';

export type DocumentRow = {
  label: string;
  value: string;
  isPrimary?: boolean;
  caption?: string;
};

export type DocumentData = {
  title: string;
  subtitle?: string;
  alignment: string;
  logo?: {
    url: string;
    width: number;
    height: number;
  };
  companyInfo?: {
    html: string;
    lines: string[];
  };
  inputDataRows: DocumentRow[];
  resultsTitle: string;
  resultsRows: DocumentRow[];
};

export function usePrepareDocumentData() {
  const { t } = useLingui();
  const {
    headerTitle,
    headerCaption,
    headerAlignment,
    resultHeading,
    resultDocumentLogo,
    resultDocumentCompanyInfo,
    thousandsSeparator,
    decimalSeparator
  } = useSettings();

  const calculations = useCalculations();
  const { displayedFields, displayedCalculations } =
    useCalculatorConditionalLogic();
  const formState = useFormSelector();

  return useCallback((): DocumentData => {
    const visibleResults = displayedCalculations.filter(
      ({ visible }) => visible
    );

    const visibleFields = displayedFields.filter(
      (field) => field.type !== 'heading'
    );

    // Get form values - map by field ID with proper formatting
    const formValuesByFieldId: Record<string, string> = {};

    visibleFields.forEach((field) => {
      const fieldId = field.id;
      if (!fieldId || !formState.fields[fieldId]) {
        return;
      }

      const fieldValue = formState.fields[fieldId].value;

      // Handle different field types
      if (field.type === 'number' || field.type === 'slider') {
        const numValue =
          typeof fieldValue === 'number'
            ? fieldValue
            : parseFloat(String(fieldValue));
        if (!isNaN(numValue)) {
          const format = 'format' in field ? field.format : undefined;
          formValuesByFieldId[fieldId] = formatSuffixNumber(numValue, {
            prefix: format?.prefix,
            suffix: format?.suffix,
            minimumFractionDigits: format?.decimalPlaces,
            maximumFractionDigits: format?.decimalPlaces || 5,
            thousandsSeparator,
            decimalSeparator
          });
        } else {
          formValuesByFieldId[fieldId] = String(fieldValue);
        }
      } else if (field.type === 'choice' || field.type === 'imageChoice') {
        const options = 'options' in field ? field.options : [];
        const multiple = 'multiple' in field ? field.multiple : false;

        if (multiple && Array.isArray(fieldValue)) {
          const selectedLabels = fieldValue
            .map((id: unknown) => {
              const option = options.find(
                (opt: unknown) =>
                  typeof opt === 'object' &&
                  opt !== null &&
                  'id' in opt &&
                  opt.id === id
              );

              return option && typeof option === 'object' && 'name' in option
                ? (option as { name: string }).name
                : String(id);
            })
            .filter(Boolean);
          formValuesByFieldId[fieldId] = selectedLabels.join(', ');
        } else {
          const option = options.find(
            (opt: unknown) =>
              typeof opt === 'object' &&
              opt !== null &&
              'id' in opt &&
              opt.id === fieldValue
          );
          formValuesByFieldId[fieldId] =
            option && typeof option === 'object' && 'name' in option
              ? (option as { name: string }).name
              : '—';
        }
      } else if (field.type === 'dropdown') {
        const options = 'options' in field ? field.options : [];
        const option = options.find(
          (opt: unknown) =>
            typeof opt === 'object' &&
            opt !== null &&
            'id' in opt &&
            opt.id === fieldValue
        );
        formValuesByFieldId[fieldId] =
          option && typeof option === 'object' && 'name' in option
            ? (option as { name: string }).name
            : String(fieldValue);
      } else {
        if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
          formValuesByFieldId[fieldId] = String(fieldValue);
        } else if (Array.isArray(fieldValue)) {
          formValuesByFieldId[fieldId] = fieldValue
            .map((v: unknown) => {
              if (typeof v === 'object' && v !== null && 'name' in v) {
                return (v as { name: string }).name;
              }

              return String(v);
            })
            .join(', ');
        } else {
          formValuesByFieldId[fieldId] = String(fieldValue);
        }
      }
    });

    // Build input data rows
    const inputDataRows: DocumentRow[] = [];
    visibleFields.forEach((field) => {
      if (field.type === 'heading') {
        return;
      }

      const fieldLabel =
        'label' in field
          ? field.label
          : 'heading' in field
            ? field.heading
            : '';
      const fieldId = field.id;
      const fieldValue =
        fieldId && formValuesByFieldId[fieldId]
          ? formValuesByFieldId[fieldId]
          : '—';

      inputDataRows.push({
        label: fieldLabel,
        value: fieldValue
      });
    });

    // Build results rows
    const resultsRows: DocumentRow[] = [];
    visibleResults.forEach((calculation) => {
      const value = calculations[calculation.id];
      const derivedValue =
        calculation.format?._mode !== 'string' && isFinite(Number(value))
          ? Number(value)
          : value;

      resultsRows.push({
        label: calculation.name || 'Result',
        value: formatCalculatorValue({
          value: derivedValue,
          format: calculation.format,
          thousandsSeparator,
          decimalSeparator
        }),
        isPrimary: calculation.resultType === 'primaryResult',
        caption: calculation.caption || undefined
      });
    });

    // Parse company info lines
    let companyInfoLines: string[] = [];
    if (resultDocumentCompanyInfo) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = resultDocumentCompanyInfo;

      tempDiv.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) {
            companyInfoLines.push(text);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          const text = element.textContent?.trim();
          if (text) {
            companyInfoLines.push(text);
          }
        }
      });
    }

    return {
      title: headerTitle || t`Calculation Results`,
      subtitle: headerCaption || undefined,
      alignment: headerAlignment,
      logo: resultDocumentLogo?.url
        ? {
            url: resultDocumentLogo.url,
            width: resultDocumentLogo.width,
            height: resultDocumentLogo.height
          }
        : undefined,
      companyInfo: resultDocumentCompanyInfo
        ? {
            html: resultDocumentCompanyInfo,
            lines: companyInfoLines
          }
        : undefined,
      inputDataRows,
      resultsTitle: resultHeading || t`Results`,
      resultsRows
    };
  }, [
    displayedCalculations,
    displayedFields,
    formState,
    calculations,
    headerTitle,
    headerCaption,
    headerAlignment,
    resultHeading,
    resultDocumentLogo,
    resultDocumentCompanyInfo,
    thousandsSeparator,
    decimalSeparator,
    t
  ]);
}
